import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const InterestRate = () => {
  // Pricing elements from the content
  const pricingElements = [
    {
      title: "Processing Fees",
      description: "One-time fee for loan processing and verification",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      )
    },
    {
      title: "Service Charges",
      description: "Nominal charges for ongoing account management",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
        </svg>
      )
    },
    {
      title: "Convenience Fees",
      description: "Optional fees based on service nature and delivery",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    }
  ];

  // Pricing factors
  const pricingFactors = [
    { factor: "Loan Tenure", icon: "📅" },
    { factor: "Risk Profile", icon: "🛡️" },
    { factor: "Operational Costs", icon: "⚙️" },
    { factor: "Service Type", icon: "💼" }
  ];

  // Transparency principles
  const transparencyPrinciples = [
    "Clearly disclosed prior to disbursement",
    "No hidden charges ever",
    "Designed for sustainability & fairness",
    "Inspired by microfinance best practices"
  ];

  return (
    <section className="relative w-full min-h-screen bg-[#0A1620] overflow-hidden">
      {/* Dark gradient background (same as HeroSection) */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[#0A1620]"></div>
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-[#1B4D5E]/20 rounded-full blur-[120px]"></div>
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-[#1A3B4D]/20 rounded-full blur-[100px]"></div>
        
        {/* Subtle grid */}
        <div className="absolute inset-0 opacity-[0.03]">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(to right, #2D5F7A 1px, transparent 1px), linear-gradient(to bottom, #2D5F7A 1px, transparent 1px)`,
              backgroundSize: "60px 60px",
            }}
          ></div>
        </div>

        {/* Wave decorations */}
        <svg className="absolute top-0 left-0 w-full h-48 rotate-180" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" preserveAspectRatio="none">
          <path fill="#2D5F7A" fillOpacity="0.08" d="M0,192L48,176C96,160,192,128,288,128C384,128,480,160,576,176C672,192,768,192,864,176C960,160,1056,128,1152,128C1248,128,1344,160,1392,176L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
        </svg>

        <svg className="absolute bottom-0 right-0 w-full h-48" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" preserveAspectRatio="none">
          <path fill="#2D5F7A" fillOpacity="0.08" d="M0,192L48,176C96,160,192,128,288,128C384,128,480,160,576,176C672,192,768,192,864,176C960,160,1056,128,1152,128C1248,128,1344,160,1392,176L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
        </svg>
      </div>

      {/* Main Content */}
      <div className="relative z-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 w-full py-24 lg:py-28">
          
          {/* Header Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 bg-[#1E3B4A]/30 px-4 py-2 rounded-full border border-[#2D5F7A]/30 backdrop-blur-sm mb-6">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#4D9ABF] opacity-40"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#4D9ABF]"></span>
              </span>
              <span className="text-[#B2D9F0] text-xs font-bold uppercase tracking-wider">
                TRANSPARENT • FAIR • CLEAR
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
              Simple & <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#7BC4E0] to-[#4D9ABF]">Transparent</span> Pricing
            </h1>
            
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-xl text-[#CCE6F5] font-light max-w-3xl mx-auto leading-relaxed"
            >
              SalaryMates offers short-term financial assistance solutions designed for salaried 
              individuals who require timely financial support.
            </motion.p>
          </motion.div>

          {/* Mission Statement Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-gradient-to-br from-[#1A2F3A] to-[#0F2128] rounded-[24px] p-8 mb-12 border border-[#2D5F7A]/30"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-[#1E3B4A] flex items-center justify-center flex-shrink-0 border border-[#7BC4E0]/30">
                <svg className="w-6 h-6 text-[#7BC4E0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-white mb-3">Our Pricing Philosophy</h2>
                <p className="text-[#CCE6F5] text-lg leading-relaxed">
                  SalaryMates follows a responsible pricing approach inspired by microfinance best 
                  practices, where charges are structured to support operations without compromising 
                  transparency or customer understanding.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Pricing Elements Grid */}
          <motion.div
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: { staggerChildren: 0.15, delayChildren: 0.5 }
              }
            }}
            initial="hidden"
            animate="visible"
            className="grid md:grid-cols-3 gap-6 mb-12"
          >
            {pricingElements.map((item, index) => (
              <motion.div
                key={index}
                variants={{
                  hidden: { opacity: 0, y: 30 },
                  visible: { opacity: 1, y: 0 }
                }}
                className="bg-gradient-to-br from-[#1A2F3A] to-[#0F2128] rounded-[20px] p-6 border border-[#2D5F7A]/30 hover:border-[#7BC4E0]/50 transition-all duration-300 group"
              >
                <div className="w-16 h-16 rounded-full bg-[#1E3B4A] flex items-center justify-center mb-4 border border-[#2D5F7A]/30 group-hover:border-[#7BC4E0]/50 transition-all">
                  <div className="text-[#7BC4E0] group-hover:scale-110 transition-transform">
                    {item.icon}
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-[#8FAFC7] text-sm">{item.description}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* Factors & Transparency Section */}
          <div className="grid lg:grid-cols-2 gap-6 mb-12">
            {/* Pricing Factors */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.8 }}
              className="bg-gradient-to-br from-[#1A2F3A] to-[#0F2128] rounded-[20px] p-6 border border-[#2D5F7A]/30"
            >
              <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <span className="w-1 h-6 bg-[#7BC4E0] rounded-full"></span>
                Pricing Factors
              </h3>
              <p className="text-[#CCE6F5] text-sm mb-4">
                All applicable charges are determined based on:
              </p>
              <div className="grid grid-cols-2 gap-3">
                {pricingFactors.map((item, index) => (
                  <div key={index} className="bg-[#1E3B4A]/30 rounded-lg p-3 border border-[#2D5F7A]/20">
                    <span className="text-2xl mb-1 block">{item.icon}</span>
                    <span className="text-[#CCE6F5] text-sm">{item.factor}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Transparency Principles */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.9 }}
              className="bg-gradient-to-br from-[#1A2F3A] to-[#0F2128] rounded-[20px] p-6 border border-[#2D5F7A]/30"
            >
              <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <span className="w-1 h-6 bg-[#7BC4E0] rounded-full"></span>
                Our Transparency Promise
              </h3>
              <ul className="space-y-3">
                {transparencyPrinciples.map((principle, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-[#7BC4E0] mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-[#CCE6F5] text-sm">{principle}</span>
                  </li>
                ))}
              </ul>

              {/* Highlighted Message */}
              <div className="mt-6 bg-[#1E3B4A]/30 rounded-lg p-4 border border-[#2D5F7A]/20">
                <p className="text-[#7BC4E0] font-semibold text-sm mb-1">⭐ No Hidden Charges</p>
                <p className="text-[#8FAFC7] text-xs">
                  Customers are encouraged to review all details carefully before proceeding.
                </p>
              </div>
            </motion.div>
          </div>

          {/* Disclosure Statement */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0 }}
            className="bg-gradient-to-br from-[#1A2F3A] to-[#0F2128] rounded-[20px] p-8 border border-[#2D5F7A]/30 text-center"
          >
            <div className="flex flex-wrap justify-center gap-8 items-center">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-[#7BC4E0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span className="text-[#CCE6F5] text-sm">Clear Disclosure</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-[#7BC4E0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-[#CCE6F5] text-sm">Fair Pricing</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-[#7BC4E0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span className="text-[#CCE6F5] text-sm">No Surprises</span>
              </div>
            </div>
          </motion.div>

          {/* Footer CTA */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            className="text-center mt-12"
          >
            <Link
              to="/apply-now"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#2D5F7A] to-[#1E3B4A] text-white font-semibold rounded-lg shadow-lg hover:shadow-[#2D5F7A]/30 transition-all duration-300 hover:scale-105 border border-[#7BC4E0]/20"
            >
              Check Your Eligibility
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Bottom decorative text */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-30">
        <span className="text-[#2D5F7A] text-sm">TRANSPARENT</span>
        <span className="text-[#2D5F7A]">•</span>
        <span className="text-[#7BC4E0] text-sm">FAIR</span>
        <span className="text-[#2D5F7A]">•</span>
        <span className="text-[#2D5F7A] text-sm">RESPONSIBLE</span>
      </div>
    </section>
  );
};

export default InterestRate;